import type { RouteFragmentStatistics, RouteSegment, RouteStatisticsStreetSegment } from '@/api/main/types.gen'
import type { Feature, FeatureCollection, LineString, Point } from 'geojson'

export type RoutePlanLineVisual =
  | 'notRequired'
  | 'singleActive'
  | 'singleInactive'
  | 'solidActive'
  | 'solidInactive'
  | 'highlightCurrent'
  | 'highlightNext'
  | 'highlightNext2'

const EMPTY_POINTS: FeatureCollection<Point> = { type: 'FeatureCollection', features: [] }

function lineStringFromUnknown(value: unknown): LineString | null {
  if (!value || typeof value !== 'object') return null
  const geometry = value as { type?: unknown; coordinates?: unknown }
  if (geometry.type !== 'LineString' || !Array.isArray(geometry.coordinates)) return null
  const coordinates: Array<[number, number]> = []
  for (const pair of geometry.coordinates) {
    if (!Array.isArray(pair) || pair.length < 2) return null
    const longitude = pair[0]
    const latitude = pair[1]
    if (typeof longitude !== 'number' || typeof latitude !== 'number') return null
    coordinates.push([longitude, latitude])
  }
  if (coordinates.length < 2) return null
  return { type: 'LineString', coordinates }
}

function positionsBySegment(routePlan: Array<RouteSegment>): Map<string, number[]> {
  const positions = new Map<string, number[]>()
  routePlan.forEach((segment, index) => {
    const existing = positions.get(segment.street_segment_id)
    if (existing) {
      existing.push(index)
    } else {
      positions.set(segment.street_segment_id, [index])
    }
  })
  return positions
}

export function highlightedRouteSegmentIds(routePlan: Array<RouteSegment>, highlightedSegment: number): string[] {
  const ids: string[] = []
  for (let offset = 0; offset < 3; offset += 1) {
    const segment = routePlan[highlightedSegment + offset]
    if (segment) ids.push(segment.street_segment_id)
  }
  return ids
}

export function routePlanLineVisual(input: {
  streetSegmentId: string
  requestedPasses: number
  requestedReversePasses: number
  positions: number[]
  fragment: Pick<RouteFragmentStatistics, 'start_index' | 'end_index'> | undefined
  highlightedSegmentIds: string[]
}): RoutePlanLineVisual {
  const { highlightedSegmentIds, streetSegmentId, positions, fragment } = input
  if (highlightedSegmentIds[2] === streetSegmentId) return 'highlightNext2'
  if (highlightedSegmentIds[1] === streetSegmentId) return 'highlightNext'
  if (highlightedSegmentIds[0] === streetSegmentId) return 'highlightCurrent'

  const start = fragment?.start_index
  const end = fragment?.end_index
  const inactive = positions.every(
    index => (start !== undefined && index < start) || (end !== undefined && index >= end)
  )
  const passes = Math.max(input.requestedPasses, input.requestedReversePasses)
  if (passes === 0) return 'notRequired'
  if (passes === 1) return inactive ? 'singleInactive' : 'singleActive'
  return inactive ? 'solidInactive' : 'solidActive'
}

export function routePlanToFeatureCollection(input: {
  streetSegments: Record<string, RouteStatisticsStreetSegment>
  routePlan: Array<RouteSegment>
  fragment: Pick<RouteFragmentStatistics, 'start_index' | 'end_index'> | undefined
  highlightedSegment: number
}): FeatureCollection<LineString> {
  const positions = positionsBySegment(input.routePlan)
  const highlightedSegmentIds = highlightedRouteSegmentIds(input.routePlan, input.highlightedSegment)
  const features: Array<Feature<LineString>> = []

  for (const segment of Object.values(input.streetSegments)) {
    const geometry = lineStringFromUnknown(segment.segment_geojson)
    if (!geometry) continue
    features.push({
      type: 'Feature',
      properties: {
        visual: routePlanLineVisual({
          streetSegmentId: segment.street_segment_id,
          requestedPasses: segment.requested_passes,
          requestedReversePasses: segment.requested_reverse_passes,
          positions: positions.get(segment.street_segment_id) ?? [],
          fragment: input.fragment,
          highlightedSegmentIds,
        }),
      },
      geometry,
    })
  }

  return { type: 'FeatureCollection', features }
}

export function fragmentEndpointsToFeatureCollection(
  fragment: Pick<RouteFragmentStatistics, 'start_point' | 'end_point'> | undefined
): FeatureCollection<Point> {
  if (!fragment) return EMPTY_POINTS
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: { role: 'start' },
        geometry: {
          type: 'Point',
          coordinates: [fragment.start_point.longitude, fragment.start_point.latitude],
        },
      },
      {
        type: 'Feature',
        properties: { role: 'end' },
        geometry: {
          type: 'Point',
          coordinates: [fragment.end_point.longitude, fragment.end_point.latitude],
        },
      },
    ],
  }
}

export function firstSegmentCenter(
  streetSegments: Record<string, Pick<RouteStatisticsStreetSegment, 'segment_geojson'>>
): { longitude: number; latitude: number } | null {
  for (const segment of Object.values(streetSegments)) {
    const line = lineStringFromUnknown(segment.segment_geojson)
    const first = line?.coordinates[0]
    if (first) return { longitude: first[0], latitude: first[1] }
  }
  return null
}
